import type { ReactNode } from 'react';
import { useFundTrades } from '@/hooks/useFundTrades';
import { useAppQueryParams } from '@/hooks/useAppQueryParams';
import { getCurrencyDisplayClass, getCurrencyString, getTimeFromMilitaryTime } from '@/lib/formatting';
import { ApiError } from '@/lib/errors';

/**
 * Port of fund-trades.component. The Angular version put up a full-screen
 * LoadingController overlay while it fetched; an inline row is less jarring
 * when you are expanding one fund inside a long list.
 */
export function FundTrades({ fundId }: { fundId: string }) {
  const { activityDate, tenant } = useAppQueryParams();
  // Note: read through the query object rather than destructuring `data`.
  // TanStack Query narrows `data` off isPending/isError, and destructuring
  // discards that correlation, leaving `data` possibly undefined.
  const tradesQuery = useFundTrades({
    fundId,
    tradeDateAsYyyyMmDd: activityDate,
    tenant,
  });

  if (tradesQuery.isPending) {
    return <TradesPanelMessage>Loading trades...</TradesPanelMessage>;
  }

  if (tradesQuery.isError) {
    return (
      <TradesPanelMessage tone="error">
        {tradesQuery.error instanceof ApiError
          ? tradesQuery.error.userMessage
          : 'Could not load trades.'}
      </TradesPanelMessage>
    );
  }

  const trades = tradesQuery.data;

  if (trades.length === 0) {
    return <TradesPanelMessage>No trades for this fund.</TradesPanelMessage>;
  }

  return (
    /*
     * Three levels of separation, so the header can't be mistaken for data:
     * the panel is a bordered card against the page, the header sits on a
     * grey band with a heavier rule beneath it, and the rows are white with
     * hairlines between them. Bold text alone wasn't enough.
     */
    <div className="mb-2 overflow-hidden rounded-lg border border-line bg-raised text-sm">
      <div className="grid grid-cols-12 gap-1 border-b-2 border-line-strong bg-band px-2 py-2 font-semibold text-content-secondary md:gap-2">
        <div className="hidden md:col-span-2 md:block" />
        <div className="col-span-5 md:col-span-4">
          Intermediary <span className="hidden font-normal text-muted md:inline">(and Dealer)</span>
        </div>
        <div className="col-span-4 text-right md:col-span-2">Amount</div>
        <div className="col-span-3 text-right md:col-span-2">
          <span className="lg:hidden">Rcvd</span>
          <span className="hidden lg:inline">Received at</span>
        </div>
      </div>

      <ul className="divide-y divide-line">
        {trades.map((trade) => (
          <li
            key={trade.tradeId}
            className="grid grid-cols-12 items-center gap-1 px-2 py-2 text-content md:gap-2"
          >
            <div className="hidden md:col-span-2 md:block" />
            <div className="col-span-5 md:col-span-4">
              {trade.intermediaryName}
              {trade.dealerName && (
                <span className="hidden pl-1.5 text-muted md:inline">
                  (Dealer: {trade.dealerName})
                </span>
              )}
            </div>
            <div className="col-span-4 text-right md:col-span-2">
              <span
                className={`${getCurrencyDisplayClass(trade.amount)} whitespace-nowrap tabular-nums`}
              >
                {getCurrencyString(trade.amount)}
              </span>
            </div>
            <div className="col-span-3 text-right md:col-span-2">
              {getTimeFromMilitaryTime(trade.tradeTime)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Keeps the panel's outline while there is nothing to put in it. */
function TradesPanelMessage({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      className={`mb-2 rounded-lg border border-line bg-raised px-4 py-3 text-sm ${
        tone === 'error' ? 'text-money-negative' : 'text-muted'
      }`}
    >
      {children}
    </p>
  );
}
